import { createContext, useContext, useState, useRef, HTMLAttributes } from 'react';
import styles from './StackableDrawerBody.module.scss';
import { CSSTransition } from 'react-transition-group';
import { joinClassNames } from '../../../utils';
import { AnimatedDiv } from '../../../internalComponents/AnimatedDiv';
import { DrawerContext } from '../DrawerContext';

export type StackContextProps = {
  history: string[];
  push: (id: string) => void;
  pop: () => void;
  transitionDuration: number;
};

const StackContext = createContext<StackContextProps>({
  history: [],
  push: () => {},
  pop: () => {},
  transitionDuration: 300,
});

type StackProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * The starting state of the stack.
   * @default []
   */
  initialStack?: string[];
};

export const StackableDrawerBody = ({ children, className, initialStack, ...props }: StackProps) => {
  const classNames = joinClassNames(styles.stack, className);
  const { transitionDuration } = useContext(DrawerContext);
  const [history, setHistory] = useState<string[]>(initialStack ?? []);

  const pop = () => setHistory((s) => s.slice(0, -1));
  const push = (loc: string) => setHistory((state) => [...state, loc]);

  const value = {
    history,
    push,
    pop,
    transitionDuration: transitionDuration ?? 300,
  };

  return (
    <StackContext.Provider value={value}>
      <div className={classNames} {...props}>
        {children}
      </div>
    </StackContext.Provider>
  );
};

export const useStack = () => {
  const ctx = useContext(StackContext);

  if (ctx === undefined) {
    throw new Error('useStack must be used within <Stack />');
  }

  return ctx;
};

type StackItemProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * The identifier of the stack item. Is used to determine if the item should show.
   * @required
   */
  name: string;
  /**
   * Whether or not this item is the root item. The root item is shown when nothing else is on the stack.
   * @default false
   */
  root?: boolean;
};

export const StackItem = ({ name, className, children, root, ...props }: StackItemProps) => {
  const { history, transitionDuration } = useStack();
  const nodeRef = useRef(null);

  if (root) {
    return (
      <div className={joinClassNames(styles.stackItem, styles['stackItem-root'], className)} {...props}>
        {children}
      </div>
    );
  }

  // make sure stack displays in correct order
  const zIndex = history.lastIndexOf(name) !== -1 ? history.lastIndexOf(name) + 1 : history.length + 1;

  return (
    <CSSTransition
      mountOnEnter
      unmountOnExit
      appear
      in={history.includes(name)}
      timeout={transitionDuration}
      nodeRef={nodeRef}
      classNames={{
        enter: styles['stackItem-enter'],
        enterDone: styles['stackItem-enterDone'],
      }}
    >
      <AnimatedDiv
        className={joinClassNames(styles.stackItem, className)}
        style={{ zIndex }}
        transitionDuration={transitionDuration}
        {...props}
        ref={nodeRef}
      >
        {children}
      </AnimatedDiv>
    </CSSTransition>
  );
};
